import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Plus, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const PricingEditor = ({ content, onSave }) => {
  const [formData, setFormData] = useState({
    section_label: "",
    title: "",
    subtitle: "",
    plans: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setFormData(content);
    }
  }, [content]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePlanChange = (index, field, value) => {
    const updated = [...formData.plans];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, plans: updated });
  };

  const handleFeatureChange = (planIndex, featureIndex, value) => {
    const updated = [...formData.plans];
    updated[planIndex].features[featureIndex] = value;
    setFormData({ ...formData, plans: updated });
  };

  const addFeature = (planIndex) => {
    const updated = [...formData.plans];
    updated[planIndex].features.push("Nueva característica");
    setFormData({ ...formData, plans: updated });
  };

  const removeFeature = (planIndex, featureIndex) => {
    const updated = [...formData.plans];
    updated[planIndex].features = updated[planIndex].features.filter((_, i) => i !== featureIndex);
    setFormData({ ...formData, plans: updated });
  };

  const addPlan = () => {
    const newPlan = {
      id: `plan-${Date.now()}`,
      name: "Nuevo Plan",
      price: 0,
      description: "Descripción del plan",
      features: ["Característica 1"],
      featured: false
    };
    setFormData({ ...formData, plans: [...formData.plans, newPlan] });
  };

  const removePlan = (index) => {
    const updated = formData.plans.filter((_, i) => i !== index);
    setFormData({ ...formData, plans: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);
    if (success) {
      toast.success("Precios actualizados correctamente");
    } else {
      toast.error("Error al guardar los cambios");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="pricing-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Editar Precios</h1>
          <p className="text-[#94a3b8]">Administra tus planes y precios</p>
        </div>
        <button onClick={addPlan} className="btn-secondary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          <span>Agregar Plan</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Textos de la Sección</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Etiqueta</label>
              <input type="text" name="section_label" value={formData.section_label} onChange={handleChange} className="contact-input w-full" />
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Título</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="contact-input w-full" />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-[#94a3b8] text-sm mb-2">Subtítulo</label>
            <textarea name="subtitle" value={formData.subtitle} onChange={handleChange} rows={2} className="contact-input w-full resize-none" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {formData.plans.map((plan, index) => (
            <div key={plan.id} className={`glass-card p-6 ${plan.featured ? "border-2 border-[#3b82f6]" : ""}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePlanChange(index, "featured", !plan.featured)}
                    className={`p-1 rounded ${plan.featured ? "text-yellow-400" : "text-[#94a3b8]"}`}
                  >
                    <Star className={`w-5 h-5 ${plan.featured ? "fill-current" : ""}`} />
                  </button>
                  <span className="text-white font-medium">{plan.featured ? "Destacado" : "Normal"}</span>
                </div>
                <button type="button" onClick={() => removePlan(index)} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[#94a3b8] text-sm mb-2">Nombre</label>
                  <input type="text" value={plan.name} onChange={(e) => handlePlanChange(index, "name", e.target.value)} className="contact-input w-full" />
                </div>
                <div>
                  <label className="block text-[#94a3b8] text-sm mb-2">Precio (USD/mes)</label>
                  <input type="number" value={plan.price} onChange={(e) => handlePlanChange(index, "price", parseFloat(e.target.value))} className="contact-input w-full" step="0.01" />
                </div>
                <div>
                  <label className="block text-[#94a3b8] text-sm mb-2">Descripción</label>
                  <textarea value={plan.description} onChange={(e) => handlePlanChange(index, "description", e.target.value)} rows={2} className="contact-input w-full resize-none" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[#94a3b8] text-sm">Características</label>
                    <button type="button" onClick={() => addFeature(index)} className="text-[#3b82f6] text-sm hover:underline">+ Agregar</button>
                  </div>
                  <div className="space-y-2">
                    {plan.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center gap-2">
                        <input type="text" value={feature} onChange={(e) => handleFeatureChange(index, fIndex, e.target.value)} className="contact-input flex-1 text-sm" />
                        <button type="button" onClick={() => removeFeature(index, fIndex)} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>{saving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default PricingEditor;
